/**
 * Hook for calculating ANI swap quotes
 *
 * This hook handles the special 2-leg swap flow for ANI:
 * - BUY: Token -> hyUSD (DEX) -> ANI (Handler)
 * - SELL: ANI -> hyUSD (Handler) -> Token (DEX)
 */

import { JsonRpcProvider } from '@ethersproject/providers'
import { Currency, CurrencyAmount, Token, TradeType } from '@uniswap/sdk-core'
import { Route as V3Route } from '@uniswap/v3-sdk'
import { useWeb3React } from '@web3-react/core'
import Decimal from 'decimal.js'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { SupportedChainId } from '../constants/chains'
import { ANI, HYUSD } from '../constants/tokens'
import {
  AniDecimal,
  AniGlobalParams,
  calculateAniFromHyUsd,
  calculateHyUsdFromAni,
  calculateMaxAniForBudget,
  detectAniSwap,
  fetchAniGlobalParams,
  HYUSD_BUFFER,
  isDirectAniSwap,
  ONE_E18,
} from '../lib/ani'
import { TradeState } from '../state/routing/types'
import { AniQuoteResult } from '../types/ani'
import { useBestTrade } from './useBestTrade'

// Re-export AniQuoteResult for existing imports
export type { AniQuoteResult } from '../types/ani'

/**
 * Cache for global params to avoid excessive RPC calls
 * Includes chainId in cache key to invalidate on network change
 */
interface ParamsCache {
  params: AniGlobalParams | null
  timestamp: number
  chainId: number | null
}

let paramsCache: ParamsCache = {
  params: null,
  timestamp: 0,
  chainId: null,
}
const PARAMS_CACHE_DURATION = 30000 // 30 seconds

/**
 * Hook to fetch and cache ANI global parameters
 * Handles race conditions, network changes, and async cleanup
 */
// eslint-disable-next-line import/no-unused-modules
export function useAniGlobalParams(): {
  params: AniGlobalParams | null
  loading: boolean
  error: Error | null
  refresh: () => void
} {
  const { provider, chainId } = useWeb3React()
  const [params, setParams] = useState<AniGlobalParams | null>(paramsCache.params)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Clear cache if chainId changes
  useEffect(() => {
    if (chainId !== paramsCache.chainId) {
      paramsCache = { params: null, timestamp: 0, chainId: chainId ?? null }
      setParams(null)
    }
  }, [chainId])

  const fetchParams = useCallback(async () => {
    if (!provider || chainId !== SupportedChainId.HYDRA) {
      return
    }

    const now = Date.now()
    // Check if cache is valid and for the same chain
    if (paramsCache.params && paramsCache.chainId === chainId && now - paramsCache.timestamp < PARAMS_CACHE_DURATION) {
      setParams(paramsCache.params)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const jsonRpcProvider = provider as JsonRpcProvider
      const newParams = await fetchAniGlobalParams(jsonRpcProvider)
      paramsCache = {
        params: newParams,
        timestamp: now,
        chainId: chainId ?? null,
      }
      setParams(newParams)
    } catch (e) {
      setError(e as Error)
      console.error('Failed to fetch ANI params:', e)
    } finally {
      setLoading(false)
    }
  }, [provider, chainId])

  const refresh = useCallback(() => {
    paramsCache = { params: null, timestamp: 0, chainId: chainId ?? null }
    fetchParams()
  }, [fetchParams, chainId])

  useEffect(() => {
    let isMounted = true

    const doFetch = async () => {
      if (!provider || chainId !== SupportedChainId.HYDRA) {
        return
      }

      const now = Date.now()
      if (
        paramsCache.params &&
        paramsCache.chainId === chainId &&
        now - paramsCache.timestamp < PARAMS_CACHE_DURATION
      ) {
        if (isMounted) setParams(paramsCache.params)
        return
      }

      if (isMounted) setLoading(true)
      if (isMounted) setError(null)

      try {
        const jsonRpcProvider = provider as JsonRpcProvider
        const newParams = await fetchAniGlobalParams(jsonRpcProvider)
        paramsCache = {
          params: newParams,
          timestamp: now,
          chainId: chainId ?? null,
        }
        if (isMounted) setParams(newParams)
      } catch (e) {
        if (isMounted) setError(e as Error)
        console.error('Failed to fetch ANI params:', e)
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    doFetch()

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false
    }
  }, [provider, chainId])

  return { params, loading, error, refresh }
}

/**
 * Main hook for ANI quote calculation
 *
 * @param inputCurrency - The input currency
 * @param outputCurrency - The output currency
 * @param typedAmount - The amount user typed (in the field they're editing)
 * @param independentField - Which field user is typing in ('INPUT' or 'OUTPUT')
 */
export function useAniQuote(
  inputCurrency: Currency | null | undefined,
  outputCurrency: Currency | null | undefined,
  typedAmount: CurrencyAmount<Currency> | undefined,
  independentField: 'INPUT' | 'OUTPUT'
): AniQuoteResult {
  const { chainId } = useWeb3React()
  const { params, loading: paramsLoading } = useAniGlobalParams()

  // Detect if this is an ANI swap
  // No useMemo needed - these return primitive values (string/boolean)
  const direction = detectAniSwap(inputCurrency, outputCurrency)
  const isDirectSwap = isDirectAniSwap(inputCurrency, outputCurrency)

  // For non-direct swaps, we need DEX quotes for the token <-> hyUSD leg
  // Determine which DEX quote we need based on direction and independent field
  const dexQuoteParams = useMemo(() => {
    if (!direction || isDirectSwap || !typedAmount) {
      return {
        tradeType: TradeType.EXACT_INPUT,
        amount: undefined,
        otherCurrency: undefined,
      }
    }

    // Helper to calculate hyUSD from ANI amount (for cases where user types ANI)
    // This needs params to do the simulation
    const calculateHyUsdFromAniAmount = (
      aniAmount: CurrencyAmount<Currency>,
      isBuy: boolean
    ): CurrencyAmount<Token> | undefined => {
      if (!params) return undefined

      const ONE_E18_DEC = new AniDecimal(ONE_E18)
      const aniWei = new AniDecimal(aniAmount.quotient.toString())

      // Calculate USDQ from ANI simulation
      const usdqWei = calculateHyUsdFromAni(aniWei, isBuy, params)

      // Convert USDQ to hyUSD using oracle rate
      const hyUsdWei = usdqWei.times(ONE_E18_DEC).div(params.oracleRate)

      // Apply buffer
      const hyUsdWithBuffer = isBuy
        ? hyUsdWei.plus(HYUSD_BUFFER) // BUY: need more hyUSD (exact output)
        : AniDecimal.max(hyUsdWei.minus(HYUSD_BUFFER), new AniDecimal(0)) // SELL: get less hyUSD (exact input)

      return CurrencyAmount.fromRawAmount(HYUSD, hyUsdWithBuffer.toFixed(0))
    }

    if (direction === 'buy') {
      // Buying ANI: need Token -> hyUSD quote
      if (independentField === 'INPUT') {
        // User typed token amount, need hyUSD output
        return {
          tradeType: TradeType.EXACT_INPUT,
          amount: typedAmount,
          otherCurrency: HYUSD as Currency,
        }
      } else {
        // User typed ANI amount, calculate hyUSD needed, then get token input
        // DEX: Token (input) <- hyUSD (exact output)
        const hyUsdAmount = calculateHyUsdFromAniAmount(typedAmount, true)
        return {
          tradeType: TradeType.EXACT_OUTPUT,
          amount: hyUsdAmount,
          otherCurrency: inputCurrency ?? undefined,
        }
      }
    } else {
      // Selling ANI: need hyUSD -> Token quote
      if (independentField === 'OUTPUT') {
        // User typed token amount, need hyUSD input
        return {
          tradeType: TradeType.EXACT_OUTPUT,
          amount: typedAmount,
          otherCurrency: HYUSD as Currency,
        }
      } else {
        // User typed ANI amount, calculate hyUSD output, then get token output
        // DEX: hyUSD (exact input) -> Token (output)
        const hyUsdAmount = calculateHyUsdFromAniAmount(typedAmount, false)
        return {
          tradeType: TradeType.EXACT_INPUT,
          amount: hyUsdAmount,
          otherCurrency: outputCurrency ?? undefined,
        }
      }
    }
  }, [direction, isDirectSwap, typedAmount, independentField, params, inputCurrency, outputCurrency])

  // Get DEX quote (only for non-direct swaps)
  const { trade: dexTrade, state: dexTradeState } = useBestTrade(
    dexQuoteParams.tradeType,
    dexQuoteParams.amount,
    dexQuoteParams.otherCurrency
  )

  // Calculate ANI quote
  const quoteResult = useMemo((): AniQuoteResult => {
    // Default result
    const defaultResult: AniQuoteResult = {
      aniAmount: undefined,
      hyUsdAmount: undefined,
      tokenAmount: undefined,
      usdqAmount: undefined,
      state: TradeState.INVALID,
      direction,
      isDirectSwap,
      quotedFromInput: independentField === 'INPUT',
      dexRoute: undefined,
      priceImpact: undefined,
      executionPrice: undefined,
    }

    // Not an ANI swap
    if (!direction) {
      return defaultResult
    }

    // Not on mainnet
    if (chainId !== SupportedChainId.HYDRA) {
      return { ...defaultResult, state: TradeState.NO_ROUTE_FOUND }
    }

    // Loading params
    if (paramsLoading || !params) {
      return { ...defaultResult, state: TradeState.LOADING }
    }

    // No amount specified
    if (!typedAmount || typedAmount.equalTo(0)) {
      return defaultResult
    }

    try {
      // CRITICAL: Convert hyUSD to USDQ using oracle rate
      // The ANI contract works with USDQ internally, not hyUSD directly
      // oracleRate is rateA from rateWithContext (1e18 scale)
      // e.g., if rateA = 1.17e18, then 1 hyUSD worth of backing = 1.17 USDQ
      const ONE_E18_DEC = new AniDecimal(ONE_E18)

      // Helper to convert hyUSD wei to USDQ wei
      // hyUSD is backed by sUSDe which appreciates, so 1 hyUSD > 1 USDQ
      const hyUsdToUsdq = (hyUsdWei: Decimal): Decimal => {
        return hyUsdWei.times(params.oracleRate).div(ONE_E18_DEC)
      }

      // Helper to convert USDQ wei to hyUSD wei
      const usdqToHyUsd = (usdqWei: Decimal): Decimal => {
        return usdqWei.times(ONE_E18_DEC).div(params.oracleRate)
      }

      // Direct swap: hyUSD <-> ANI (no DEX leg)
      if (isDirectSwap) {
        const typedAmountWei = new AniDecimal(typedAmount.quotient.toString())

        if (direction === 'buy') {
          // hyUSD -> ANI
          if (independentField === 'INPUT') {
            // User typed hyUSD amount, calculate ANI output
            // Convert hyUSD directly to USDQ (no buffer for quote display - matches anistar.io)
            const usdqBudget = hyUsdToUsdq(typedAmountWei)
            const aniWei = calculateMaxAniForBudget(usdqBudget, params)

            return {
              aniAmount: CurrencyAmount.fromRawAmount(ANI, aniWei.toFixed(0)),
              hyUsdAmount: typedAmount as CurrencyAmount<Token>,
              tokenAmount: typedAmount,
              usdqAmount: usdqBudget.toFixed(0), // USDQ threshold for handler
              state: TradeState.VALID,
              direction,
              isDirectSwap,
              quotedFromInput: true,
              dexRoute: undefined, // No DEX leg for direct swap
              priceImpact: undefined, // TODO: calculate
              executionPrice: aniWei.gt(0) ? typedAmountWei.div(aniWei) : undefined,
            }
          } else {
            // User typed ANI amount, calculate hyUSD needed
            // Simulation returns USDQ, convert back to hyUSD
            const usdqWei = calculateHyUsdFromAni(typedAmountWei, true, params)
            const hyUsdWei = usdqToHyUsd(usdqWei)
            const hyUsdPlusBuffer = hyUsdWei.plus(HYUSD_BUFFER)

            return {
              aniAmount: typedAmount as CurrencyAmount<Token>,
              hyUsdAmount: CurrencyAmount.fromRawAmount(HYUSD, hyUsdPlusBuffer.toFixed(0)),
              tokenAmount: CurrencyAmount.fromRawAmount(HYUSD, hyUsdPlusBuffer.toFixed(0)),
              usdqAmount: usdqWei.toFixed(0), // USDQ threshold for handler
              state: TradeState.VALID,
              direction,
              isDirectSwap,
              quotedFromInput: false,
              dexRoute: undefined, // No DEX leg for direct swap
              priceImpact: undefined,
              executionPrice: typedAmountWei.gt(0) ? hyUsdPlusBuffer.div(typedAmountWei) : undefined,
            }
          }
        } else {
          // ANI -> hyUSD (SELL)
          if (independentField === 'INPUT') {
            // User typed ANI amount, calculate hyUSD output
            // Simulation returns USDQ, convert to hyUSD
            const usdqWei = calculateHyUsdFromAni(typedAmountWei, false, params)
            const hyUsdWei = usdqToHyUsd(usdqWei)
            const hyUsdMinusBuffer = AniDecimal.max(hyUsdWei.minus(HYUSD_BUFFER), new AniDecimal(0))

            return {
              aniAmount: typedAmount as CurrencyAmount<Token>,
              hyUsdAmount: CurrencyAmount.fromRawAmount(HYUSD, hyUsdMinusBuffer.toFixed(0)),
              tokenAmount: CurrencyAmount.fromRawAmount(HYUSD, hyUsdMinusBuffer.toFixed(0)),
              usdqAmount: usdqWei.toFixed(0), // USDQ output from handler
              state: TradeState.VALID,
              direction,
              isDirectSwap,
              quotedFromInput: true,
              dexRoute: undefined, // No DEX leg for direct swap
              priceImpact: undefined,
              executionPrice: typedAmountWei.gt(0) ? hyUsdMinusBuffer.div(typedAmountWei) : undefined,
            }
          } else {
            // User typed hyUSD amount, calculate ANI needed
            // Convert hyUSD to USDQ for simulation
            const hyUsdPlusBuffer = typedAmountWei.plus(HYUSD_BUFFER)
            const usdqTarget = hyUsdToUsdq(hyUsdPlusBuffer)
            const aniWei = calculateAniFromHyUsd(usdqTarget, false, params)

            return {
              aniAmount: CurrencyAmount.fromRawAmount(ANI, aniWei.toFixed(0)),
              hyUsdAmount: typedAmount as CurrencyAmount<Token>,
              tokenAmount: typedAmount,
              usdqAmount: usdqTarget.toFixed(0), // USDQ threshold for handler
              state: TradeState.VALID,
              direction,
              isDirectSwap,
              quotedFromInput: false,
              dexRoute: undefined, // No DEX leg for direct swap
              priceImpact: undefined,
              executionPrice: aniWei.gt(0) ? typedAmountWei.div(aniWei) : undefined,
            }
          }
        }
      }

      // Non-direct swap: need DEX leg
      // Check DEX trade state
      if (dexTradeState === TradeState.LOADING) {
        return { ...defaultResult, state: TradeState.LOADING }
      }

      // No route found if DEX returns NO_ROUTE_FOUND, INVALID, or no trade
      if (dexTradeState === TradeState.NO_ROUTE_FOUND || dexTradeState === TradeState.INVALID || !dexTrade) {
        return { ...defaultResult, state: TradeState.NO_ROUTE_FOUND }
      }

      // Extract V3 route from DEX trade for multi-hop path encoding
      // The trade may have multiple swaps, but for ANI flow we use single DEX leg
      // Cast through unknown since we know DEX uses V3 (IRoute -> V3Route)
      const extractedRoute =
        dexTrade.swaps && dexTrade.swaps.length > 0
          ? (dexTrade.swaps[0].route as unknown as V3Route<Currency, Currency>)
          : undefined

      // Calculate based on direction and independent field
      if (direction === 'buy') {
        // Token -> hyUSD -> ANI
        if (independentField === 'INPUT') {
          // User typed token amount
          // DEX gives us hyUSD output, then calculate ANI
          const hyUsdOutput = dexTrade.outputAmount
          const hyUsdWei = new AniDecimal(hyUsdOutput.quotient.toString())
          const hyUsdMinusBuffer = AniDecimal.max(hyUsdWei.minus(HYUSD_BUFFER), new AniDecimal(0))
          // Convert hyUSD to USDQ for simulation
          const usdqBudget = hyUsdToUsdq(hyUsdMinusBuffer)
          const aniWei = calculateMaxAniForBudget(usdqBudget, params)

          return {
            aniAmount: CurrencyAmount.fromRawAmount(ANI, aniWei.toFixed(0)),
            hyUsdAmount: hyUsdOutput as CurrencyAmount<Token>,
            tokenAmount: typedAmount,
            usdqAmount: usdqBudget.toFixed(0),
            state: TradeState.VALID,
            direction,
            isDirectSwap,
            quotedFromInput: true,
            dexRoute: extractedRoute,
            priceImpact: undefined,
            executionPrice: aniWei.gt(0) ? new AniDecimal(typedAmount.quotient.toString()).div(aniWei) : undefined,
          }
        } else {
          // User typed ANI amount
          // Calculate hyUSD needed, then get DEX quote for token input
          const typedAmountWei = new AniDecimal(typedAmount.quotient.toString())
          // Simulation returns USDQ, convert to hyUSD
          const usdqNeeded = calculateHyUsdFromAni(typedAmountWei, true, params)
          const hyUsdNeeded = usdqToHyUsd(usdqNeeded)
          const hyUsdPlusBuffer = hyUsdNeeded.plus(HYUSD_BUFFER)

          // We need a second DEX quote: exact output hyUSD -> token input
          // For now, return what we have; the hook will re-run when DEX quote is ready
          // This is a simplification - in production you'd want to handle this more elegantly

          return {
            aniAmount: typedAmount as CurrencyAmount<Token>,
            hyUsdAmount: CurrencyAmount.fromRawAmount(HYUSD, hyUsdPlusBuffer.toFixed(0)),
            tokenAmount: dexTrade?.inputAmount,
            usdqAmount: usdqNeeded.toFixed(0),
            state: dexTrade ? TradeState.VALID : TradeState.LOADING,
            direction,
            isDirectSwap,
            quotedFromInput: false,
            dexRoute: extractedRoute,
            priceImpact: undefined,
            executionPrice: undefined,
          }
        }
      } else {
        // ANI -> hyUSD -> Token (SELL)
        if (independentField === 'INPUT') {
          // User typed ANI amount
          // Calculate hyUSD from ANI, then get DEX quote for token output
          const typedAmountWei = new AniDecimal(typedAmount.quotient.toString())
          // Simulation returns USDQ, convert to hyUSD
          const usdqWei = calculateHyUsdFromAni(typedAmountWei, false, params)
          const hyUsdWei = usdqToHyUsd(usdqWei)
          const hyUsdMinusBuffer = AniDecimal.max(hyUsdWei.minus(HYUSD_BUFFER), new AniDecimal(0))

          // DEX quote should give us token output from hyUSD input
          // For now return what we have
          return {
            aniAmount: typedAmount as CurrencyAmount<Token>,
            hyUsdAmount: CurrencyAmount.fromRawAmount(HYUSD, hyUsdMinusBuffer.toFixed(0)),
            tokenAmount: dexTrade?.outputAmount,
            usdqAmount: usdqWei.toFixed(0),
            state: dexTrade ? TradeState.VALID : TradeState.LOADING,
            direction,
            isDirectSwap,
            quotedFromInput: true,
            dexRoute: extractedRoute,
            priceImpact: undefined,
            executionPrice: undefined,
          }
        } else {
          // User typed token amount (B.2 per Florian's spec)
          // DEX gives us hyUSD needed, then calculate ANI needed
          // Per spec: adds HYUSD_BUFFER for rounding
          const hyUsdInput = dexTrade.inputAmount
          const hyUsdWei = new AniDecimal(hyUsdInput.quotient.toString())
          const hyUsdPlusBuffer = hyUsdWei.plus(HYUSD_BUFFER)
          // Convert hyUSD to USDQ for simulation
          const usdqTarget = hyUsdToUsdq(hyUsdPlusBuffer)
          const aniWei = calculateAniFromHyUsd(usdqTarget, false, params)

          return {
            aniAmount: CurrencyAmount.fromRawAmount(ANI, aniWei.toFixed(0)),
            // Store hyUSD WITH buffer - callback will subtract buffer for DEX
            hyUsdAmount: CurrencyAmount.fromRawAmount(HYUSD, hyUsdPlusBuffer.toFixed(0)),
            tokenAmount: typedAmount,
            usdqAmount: usdqTarget.toFixed(0),
            state: TradeState.VALID,
            direction,
            isDirectSwap,
            quotedFromInput: false,
            dexRoute: extractedRoute,
            priceImpact: undefined,
            executionPrice: aniWei.gt(0) ? new AniDecimal(typedAmount.quotient.toString()).div(aniWei) : undefined,
          }
        }
      }
    } catch (e) {
      console.error('ANI quote calculation error:', e)
      return { ...defaultResult, state: TradeState.INVALID }
    }
  }, [chainId, direction, isDirectSwap, params, paramsLoading, typedAmount, independentField, dexTrade, dexTradeState])

  return quoteResult
}
